import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";

const CustomCalendar = ({ value, onChange, disabledDates = [], triggerRef, isOpen = true, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(
    value ? new Date(value) : new Date()
  );
  const [coords, setCoords] = useState(null);
  const calendarRef = useRef(null);

  const updateCoords = () => {
    const el = triggerRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
  };

  useLayoutEffect(() => {
    if (isOpen) updateCoords();
  }, [isOpen, triggerRef]);

  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => updateCoords();
    const handleOutsideClick = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target) &&
          triggerRef?.current && !triggerRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose, triggerRef]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isDateDisabled = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = formatDateString(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate 30 days from today (defensive check - backend also limits to 30 days)
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    // Disable if date is past or not in backend's available dates
    // Note: Backend ensures all available_dates are within 30 days, but we enforce it here too
    return date < today || date > thirtyDaysFromNow || disabledDates.includes(dateString);
  };

  const handleDateClick = (day) => {
    if (!isDateDisabled(day)) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      onChange(formatDateString(date));
    }
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const CalendarContent = () => (
    <div className="bg-white border border-gray-300 rounded-lg p-2 w-64 shadow-xl">
      <div className="flex items-center justify-between mb-2" 
           style={{borderBottom:"1px solid #bdbdbd"}}>
        <button
          onClick={prevMonth}
          className="px-1 py-0.5 hover:bg-gray-100 rounded transition-colors border-none bg-transparent"
          type="button"
        >
          <i className="fa-solid fa-chevron-left text-gray-600 text-xs"></i>
        </button>
        <h3 className="text-xs font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={nextMonth}
          className="px-1 py-0.5 hover:bg-gray-100 rounded transition-colors border-none bg-transparent"
          type="button"
        >
          <i className="fa-solid fa-chevron-right text-gray-600 text-xs"></i>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {weekDays.map((day) => (
          <div key={day} className="text-center text-[12px] font-semibold text-gray-600">
            {day[0]}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, idx) => (
          <button
            key={idx}
            onClick={() => day && handleDateClick(day)}
            disabled={day === null || isDateDisabled(day)}
            type="button"
            className={`px-1 py-0.5 text-[12px] rounded-full transition-colors border-none w-6 h-6  ${
              day === null
                ? "invisible"
                : isDateDisabled(day)
                ? "text-black opacity-50 cursor-not-allowed bg-transparent"
                : value === formatDateString(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))
                ? "bg-blue-600 text-white font-semibold"
                : "text-black bg-transparent hover:bg-blue-50"
            }`}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {isOpen &&
        coords &&
        createPortal(
          <div
            ref={calendarRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 10000,
            }}
          >
            <CalendarContent />
          </div>,
          document.body
        )}
    </>
  );
};

export default CustomCalendar;
